
import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { catchError, switchMap, map } from 'rxjs/operators';
import { CoresService } from '../../../core/services/cores.service';
import * as Cesium from 'cesium';
import { DataContextService } from '../../../core/services/data-context.service';
import { ArcAngelSharedService } from '../../../core/services/arc-angel-shared.service';
import { CameraService } from '../../../core/services/camera.service';
import { ArcAngelService } from '../../../core/services/arc-angel.service';
import { ArcAngelLiveModeService } from '../../../core/services/arc-angel-live-mode.service';
import { ArcAngelPlaybackModeService } from '../../../core/services/arc-angel-playback-mode.service';
import { ArcAngelMonitorModeService } from '../arc-angel-monitor-mode.service';
import { SessionOptionsService } from '../../../core/services/session-option.service';
import { OrganizationService } from '../../../core/services/organization.service';
import { OrganizationUtilService } from '../../../core/services/organization-util.service';
import { of, Subject, interval, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User } from '../../../core/models/organization.interface';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DetailsModalComponent } from '../organization/details-modal/details-modal.component';
import { MessagingService } from '../../../core/services/messaging.service';
import { NgxSpinnerService } from 'ngx-spinner';
// Add messaging service import - adjust path as needed
// import { MessagingService } from '../../../core/services/messaging.service';

@Component({
  selector: 'app-user-profile-attribute-visualizer',
  standalone: true,
  imports: [],
  templateUrl: './user-profile-attribute-visualizer.component.html',
  styleUrls: ['./user-profile-attribute-visualizer.component.css']
})
export class UserProfileAttributeVisualizerComponent implements OnInit, OnDestroy {
  @Input() entity: any;
  @Input() mapEntityId!: string;

  data: any = {
    numberTotal: 0,
    numberTotalVideo: 0,
    messageListFull: []
  };
  printLocation = '';
  isInBuilding = false;
  tempLat = 0.0;
  tempLong = 0.0;

  // Tab constants
  readonly INFO = 1;
  readonly MESSAGES = 2;
  readonly VIDEO = 3;
  show = this.INFO;
  playbackCameraSessions: any[] = [];
  isProcallPlayback: boolean = false;
  isTrackingPlayback: boolean = false;
  private tickListenerName = 'user-profile-attribute-visualizer-' + Math.random().toString(36).substring(2);
  private destroy$ = new Subject<void>();
  private associatedPerimeterSubscription?: Subscription;
  private messagingPollerSubscription?: Subscription;
  private origLocation = '';

  isLoadingBaseballcard: boolean = false;
  // Add messaging-related properties
  private eventStartTime?: string;
  private eventEndTime?: string;
  private userId?: string;

  constructor(
    private Core: CoresService,
    private camera: CameraService,
    private arcAngelSharedService: ArcAngelSharedService,
    private pluginContext: DataContextService,
    private arcAngelService: ArcAngelService,
    private arcAngelPlaybackModeService: ArcAngelPlaybackModeService,
    private arcAngelMonitorModeService: ArcAngelMonitorModeService,
    private arcAngelLiveModeService: ArcAngelLiveModeService,
    private sessionOptionsService: SessionOptionsService,
    private cdr: ChangeDetectorRef,
    private organizationService: OrganizationService,
    private modalService: NgbModal,
    private organizationUtilService: OrganizationUtilService,
    private messagingService: MessagingService,
    private spinnerService: NgxSpinnerService
  ) { }

  ngOnInit() {
    if (this.entity && this.entity.endStatus) {
      this.arcAngelSharedService.latLonToLocationGmap(this.entity.endStatus.latitude, this.entity.endStatus.longitude).then((response) => {
        this.origLocation = response;
        this.printLocation = this.origLocation;
        return;
      }).catch((response) => {
        this.origLocation = response;
        this.printLocation = this.origLocation;
      });
    }

    if (this.entity && typeof this.Core?.addOnTickListener === 'function') {
      this.Core.addOnTickListener(this.tickListenerName, this.onTick.bind(this));
    }

    // Initialize messaging
    this.initializeMessaging();

    this.startAssociatedPerimeterTimer();
  }

  ngOnDestroy() {
    if (typeof this.Core?.removeOnTickListener === 'function') {
      this.Core.removeOnTickListener(this.tickListenerName);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeMessaging(): void {
    this.userId = this.entity?.getUserDto?.()?.getId?.() || this.entity?.userDto?.id || null;

    if (this.userId) {
      this.getPeriodMessagesForUserFirstPage();
      if (!this.isPlaybackMode()) {
        this.startMessagingPoller();
      }
    }
  }

  private getPeriodMessagesForUserFirstPage(): void {
    if (!this.userId) return;

    const timeRange = this.getTimeRange();
    const startTime = timeRange.startTime;
    const endTime = timeRange.endTime;

    this.messagingService.getPeriodMessagesForUser(this.userId, 100, 1, startTime, endTime)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.data.numberTotal = results.count || 0;
          this.data.messageListFull = this.reformatMessages(results.rows || []);
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error fetching initial messages:', error);
        }
      });
  }

  private startMessagingPoller(): void {
    this.messagingPollerSubscription = interval(5000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getMorePeriodMessagesForUser();
      });
  }

  private getMorePeriodMessagesForUser(): void {
    if (!this.data.messageListFull || !this.userId) {
      return;
    }

    let startTime: string;
    let endTime: string;

    if (this.data.messageListFull.length > 0) {
      const lastMessageTime = new Date(this.data.messageListFull[0].createdAt || this.data.messageListFull[0].created_at);
      lastMessageTime.setMilliseconds(lastMessageTime.getMilliseconds() + 1);
      startTime = lastMessageTime.toISOString();
    } else if (this.eventStartTime) {
      startTime = this.eventStartTime;
    } else {
      const fiveSecondsAgo = new Date();
      fiveSecondsAgo.setSeconds(fiveSecondsAgo.getSeconds() - 5);
      startTime = fiveSecondsAgo.toISOString();
    }

    if (this.eventEndTime) {
      endTime = this.eventEndTime;
    } else {
      endTime = new Date().toISOString();
    }

    this.messagingService.getPeriodMessagesForUser(this.userId, 100, 1, startTime, endTime)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          if (results && results.rows && results.rows.length > 0) {
            const newMessages = this.reformatMessages(results.rows);
            this.data.messageListFull = [...newMessages, ...this.data.messageListFull];
            this.data.numberTotal = this.data.messageListFull.length;
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          console.error('Error fetching more messages:', error);
        }
      });
  }

  private getTimeRange(): { startTime: string; endTime: string } {
    let startTime: string;
    let endTime: string;

    if (this.isPlaybackMode()) {
      if (this.arcAngelPlaybackModeService.data?.playbackIncident) {
        startTime = this.arcAngelPlaybackModeService.data.playbackIncident.obj.createdAt;
        endTime = this.arcAngelPlaybackModeService.data.playbackIncident.obj.endAt;
      } else if (this.arcAngelPlaybackModeService.data?.playbackProcall) {
        startTime = new Date(this.arcAngelPlaybackModeService.data.playbackProcall.firstStatus.timestamp).toISOString();
        endTime = new Date(this.arcAngelPlaybackModeService.data.playbackProcall.endStatus.timestamp).toISOString();
      } else if (this.arcAngelPlaybackModeService.data?.playbackTracking) {
        startTime = this.arcAngelPlaybackModeService.data.playbackTracking.begin;
        endTime = this.arcAngelPlaybackModeService.data.playbackTracking.end;
      } else {
        const now = new Date();
        const yesterday = new Date(now.getTime() - (24 * 60 * 60 * 1000));
        startTime = yesterday.toISOString();
        endTime = now.toISOString();
      }

      this.eventEndTime = endTime;

    } else if (this.isLiveMode()) {
      endTime = new Date().toISOString();

      if (this.arcAngelLiveModeService.data?.liveIncident) {
        startTime = this.arcAngelLiveModeService.data.liveIncident.obj.createdAt;
      } else if (this.arcAngelLiveModeService.data?.liveProcall) {
        startTime = this.arcAngelLiveModeService.data.liveProcall.created;
      } else if (this.arcAngelLiveModeService.data?.liveTracking) {
        startTime = this.arcAngelLiveModeService.data.liveTracking.begin;
      } else {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        startTime = yesterday.toISOString();
      }

    } else if (this.isMonitorMode()) {
      endTime = new Date().toISOString();
      const events = this.arcAngelMonitorModeService.data?.monitorEvents?.filter((event: any) => {
        return event.eventId === this.entity?.eventId;
      }) || [];
      const procalls = this.arcAngelMonitorModeService.data?.monitorProcalls?.filter((procall: any) => {
        return procall.arcAngelId === this.entity?.procallId;
      }) || [];

      const tracking = this.entity?.activeTrackingEvent;

      if (events.length > 0) {
        const event = events[0];
        startTime = event.obj.createdAt;
      } else if (procalls.length > 0) {
        startTime = procalls[0].created;
      } else if (tracking) {
        startTime = tracking.begin;
      } else {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        startTime = yesterday.toISOString();
      }

    } else {
      const now = new Date();
      const yesterday = new Date(now.getTime() - (24 * 60 * 60 * 1000));
      startTime = yesterday.toISOString();
      endTime = now.toISOString();
    }
    this.eventStartTime = startTime;
    return { startTime, endTime };
  }

  private reformatMessages(messages: any[]): any[] {
    // Implement your message reformatting logic here
    // This replaces the original `reformat` function
    return messages.map(message => ({
      ...message,
      // Add any formatting or transformation logic here
      formatted_time: new Date(message.created_at).toLocaleString()
    }));
  }

  // Add method to set event time range if needed
  setEventTimeRange(startTime?: string, endTime?: string): void {
    this.eventStartTime = startTime;
    this.eventEndTime = endTime;

    // Refresh messages with new time range
    if (this.userId) {
      this.getPeriodMessagesForUserFirstPage();
    }
  }

  private startAssociatedPerimeterTimer(): void {
    this.associatedPerimeterSubscription = interval(5000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkAssociatedPerimeter();
      });
  }

  private checkAssociatedPerimeter(): void {
    if (this.entity?.getAssociatedPerimeter?.()) {
      this.isInBuilding = this.entity.getAssociatedPerimeter().getIsPartOfBuilding();
      this.printLocation = this.entity.getAssociatedPerimeter().getName();
    } else if (this.getPerimeterIdAndNameFromClosestBeacon(this.entity)?.id) {
      this.isInBuilding = true;
      this.printLocation = this.getPerimeterIdAndNameFromClosestBeacon(this.entity).name;
    } else {
      this.isInBuilding = false;
      this.printLocation = this.origLocation;

      const userData = {
        name: this.entity?.name || this.entity?.userEntity?.name,
        location: {
          latitude: this.entity?.endStatus?.latitude || this.entity?.userEntity?.endStatus?.latitude,
          longitude: this.entity?.endStatus?.longitude || this.entity?.userEntity?.endStatus?.longitude
        }
      };

      this.updateUserAddress(userData);
    }

    this.cdr.detectChanges();
  }

  private getPerimeterIdAndNameFromClosestBeacon(entity: any): { id: string | null, name: string } {
    let id: string | null = null;
    let name = '';

    try {
      if (entity?.closestBeacon) {
        const myPerimeters = this.organizationUtilService.data.myPerimeters;

        for (const perimeterId in myPerimeters) {
          const perimeter = myPerimeters[perimeterId];

          if (perimeter?.beacons && typeof perimeter.beacons === 'object') {
            const matchingBeacon = perimeter.beacons[entity.closestBeacon];

            if (matchingBeacon) {
              id = perimeter.arcAngelId || perimeter.id;
              name = perimeter.name || perimeter.label || '';
              break;
            }
          }

          if (id) {
            break;
          }
        }
      }

      return { id, name };
    } catch (error) {
      return { id: null, name: '' };
    }
  }

  private updateUserAddress(userData: any): void {
  }

  private onTick(clock: any) {
    if (!this.entity?.getSampledPositions) return;
    const newDate = Cesium.JulianDate.addSeconds(clock.currentTime, -1, new Cesium.JulianDate());
    let currentPosition = this.entity.getSampledPositions().getValue(newDate);

    if (!currentPosition && this.entity.getLastKnownPosition) {
      currentPosition = this.entity.getLastKnownPosition();
    }

    if (currentPosition && Cesium.Cartographic) {
      const carto = Cesium.Cartographic.fromCartesian(currentPosition);
      this.tempLat = Cesium.Math.toDegrees(carto.latitude);
      this.tempLong = Cesium.Math.toDegrees(carto.longitude);

      this.cdr.detectChanges();
    }
  }


  setShow(tab: number) {
    this.show = tab;
  }

  getShow(tab: number): boolean {
    return this.show === tab;
  }

  isPlaybackMode(): boolean {
    return this.sessionOptionsService.isPlaybackMode();
  }

  isLiveMode(): boolean {
    return this.sessionOptionsService.isLiveMode();
  }

  isMonitorMode(): boolean {
    return this.sessionOptionsService.isMonitorMode();
  }

  isPendingProcall(): boolean {
    return false;
  }
  isOpenProcall(): boolean {
    return false;
  }

  openProcall() {
  }
  resolveProcall() {
  }

  goLiveProcall() { }
  goLiveCalendar() { }
  goLiveTracking() { }
  resolveCalendar() { }
  resolveTracking() { }

  getStartTime(): string {
    let start: Date | undefined;
    if (this.isPlaybackMode()) {
      if (this.entity?.statusSamples?._times?.length > 0) {
        return Cesium.JulianDate.toDate(this.entity.statusSamples._times[0]).toUTCString();
      }
    } else if (this.isLiveMode()) {
      if (this.entity?.procallState) {
        return new Date(this.arcAngelLiveModeService.data.liveProcall.created).toUTCString();
      } else if (this.entity?.activeTrackingState) {
        return new Date(this.arcAngelLiveModeService.data.liveTracking.createdAt).toUTCString();
      }
    } else if (this.entity?.procallState) {
      const monitorProcalls = this.arcAngelMonitorModeService.data.monitorProcalls || [];
      for (const procall of monitorProcalls) {
        if (procall.arcAngelId === this.entity.procallId) {
          start = new Date(procall.created);
          break;
        }
      }
      return start ? start.toUTCString() : '';
    } else if (this.entity?.activeTrackingState) {
      start = new Date(this.entity.activeTrackingEvent.createdAt);
      return start.toUTCString();
    }
    return '';
  }

  getEndTime(): string {
    if (this.isPlaybackMode()) {
      if (this.entity?.statusSamples?._times?.length > 0) {
        return Cesium.JulianDate
          .toDate(this.entity.statusSamples._times[this.entity.statusSamples._times.length - 1])
          .toUTCString();
      }
    } else if (!this.isPlaybackMode() && this.entity?.activeTrackingState) {
      return new Date(this.entity.activeTrackingEvent.checkInTime).toUTCString();
    }
    return '';
  }

  getTotalDuration(): string {
    let start: Date | undefined;
    let end: Date | undefined;
    let startTs: number;
    let endTs: number;
    let duration: string;
    if (this.isPlaybackMode()) {
      if (this.entity?.statusSamples?._times?.length > 0) {
        start = Cesium.JulianDate.toDate(this.entity.statusSamples._times[0]);
        end = Cesium.JulianDate.toDate(
          this.entity.statusSamples._times[this.entity.statusSamples._times.length - 1]
        );
        startTs = start.getTime() / 1000;
        endTs = end.getTime() / 1000;
        duration = this.sec2str(Math.round(endTs - startTs));
        return duration;
      }
    } else if (!this.isPlaybackMode() && this.entity?.activeTrackingState) {
      start = new Date(this.entity.activeTrackingEvent.createdAt);
      end = new Date(this.entity.activeTrackingEvent.checkInTime);
      startTs = start.getTime() / 1000;
      endTs = end.getTime() / 1000;
      duration = this.sec2str(Math.round(endTs - startTs));
      return duration;
    }
    return '';
  }

  getElapsedTime(): string {
    let start: Date | undefined;
    let startTs: number;
    let endTs: number;
    let nowTs: number;
    let elapsedTime: string;
    if (this.isPlaybackMode()) {
      if (this.entity?.timestamp && this?.getCurrentTimestamp) {
        startTs = new Date(this.entity.timestamp).getTime() / 1000;
        nowTs = new Date(this.getCurrentTimestamp()).getTime() / 1000;
        elapsedTime = this.sec2str(Math.round(nowTs - startTs));
        return elapsedTime;
      }
    } else if (!this.isPlaybackMode() && this.entity?.procallState) {
      const monitorProcalls = this.arcAngelMonitorModeService.data.monitorProcalls || [];
      for (const procall of monitorProcalls) {
        if (procall.arcAngelId === this.entity.procallId) {
          start = new Date(procall.created);
          break;
        }
      }
      if (start && this.getCurrentTimestamp) {
        startTs = start.getTime() / 1000;
        nowTs = new Date(this.getCurrentTimestamp()).getTime() / 1000;
        elapsedTime = this.sec2str(Math.round(nowTs - startTs));
        return elapsedTime;
      }
      return '';
    } else if (!this.isPlaybackMode() && this.entity?.activeTrackingState) {
      startTs = new Date(this.entity.activeTrackingEvent.createdAt).getTime() / 1000;
      nowTs = new Date(this.getCurrentTimestamp()).getTime() / 1000;
      endTs = new Date(this.entity.activeTrackingEvent.checkInTime).getTime() / 1000;
      if (nowTs > endTs) {
        nowTs = endTs;
      }
      elapsedTime = this.sec2str(Math.round(nowTs - startTs));
      return elapsedTime;
    }
    return '';
  }

  getElapsedTimePct(): string {
    if (this.isPlaybackMode() && this.entity?.statusSamples?._times?.length > 0 && this?.getCurrentTimestamp) {
      const start = Cesium.JulianDate.toDate(this.entity.statusSamples._times[0]);
      const end = Cesium.JulianDate.toDate(
        this.entity.statusSamples._times[this.entity.statusSamples._times.length - 1]
      );
      const startTs = start.getTime() / 1000;
      const endTs = end.getTime() / 1000;
      const nowTs = new Date(this.getCurrentTimestamp()).getTime() / 1000;
      let pct = Math.round(((nowTs - startTs) / (endTs - startTs)) * 100);
      if (pct < 0) pct = 0;
      if (pct > 100) pct = 100;
      return pct.toString();
    }
    return '';
  }

  private sec2str(t: number): string {
    const d = Math.floor(t / 86400);
    const h = ('0' + (Math.floor(t / 3600) % 24)).slice(-2);
    const m = ('0' + (Math.floor(t / 60) % 60)).slice(-2);
    const s = ('0' + (t % 60)).slice(-2);
    let dayStr = ' day and ';
    if (d > 1) {
      dayStr = ' days and ';
    }
    return (d > 0 ? d + dayStr : '') + h + ':' + m + ':' + s;
  }

  private getCurrentTimestamp(): number {
    return Date.now();
  }

  getCalendarInfo(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = ('0' + (now.getMonth() + 1)).slice(-2);
    const day = ('0' + now.getDate()).slice(-2);
    const hours = ('0' + now.getHours()).slice(-2);
    const minutes = ('0' + now.getMinutes()).slice(-2);
    const seconds = ('0' + now.getSeconds()).slice(-2);
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  getTrackingInfo() { }
  viewPerimeter() { }
  refreshLocation() { }

  private activeModalRef: any = null;

  showUserBaseballcard(): void {
    if (this.isLoadingBaseballcard || this.activeModalRef) {
      return;
    }
    this.isLoadingBaseballcard = true;
    this.spinnerService.show();

    const title = 'User Info';
    let userId: string | null = null;
    try {
      userId = this.entity?.getUserDto?.()?.getId?.() || this.entity?.userId || null;
    } catch (e) {
      console.error('Error getting userId:', e);
    }

    if (!userId) {
      this.isLoadingBaseballcard = false;
      this.spinnerService.hide();
      return;
    }

    this.organizationService.getProfilesFromOrganization(userId).pipe(
      switchMap((userInfo: any) => {
        if (!userInfo) {
          return of({ userInfo: null, pictureBlob: null, emergencyNumber: null });
        }

        const pictureId = userInfo?.UserPictures?.[0]?.id || null;
        const organizationId = userInfo?.UserOrganizations?.[0]?.Organization?.id || null;

        return this.organizationService.getEmergencyNumber().pipe(
          switchMap((emergencyNumbers: any) => {
            const emergencyNumber = this.findEmergencyNumber(emergencyNumbers, organizationId);

            if (!pictureId) {
              return of({ userInfo, pictureBlob: null, emergencyNumber });
            }

            return this.organizationService.getUserPicture(pictureId).pipe(
              catchError((error: any) => {
                return of(null);
              }),
              map(pictureBlob => ({ userInfo, pictureBlob, emergencyNumber }))
            );
          })
        );
      })
    ).subscribe({
      next: ({ userInfo, pictureBlob, emergencyNumber }) => {
        this.isLoadingBaseballcard = false;
        this.spinnerService.hide();
        if (!userInfo || this.activeModalRef) {
          return;
        }

        userInfo.picture = pictureBlob ? URL.createObjectURL(pictureBlob) : null;
        userInfo.emergencyNumber = emergencyNumber;
        this.openDetailsModal(title, userInfo, true);
      },
      error: (err: any) => {
        this.isLoadingBaseballcard = false;
        this.spinnerService.hide();
        console.error('Error fetching user data:', err);
        this.errorModal('Error', 'Failed to fetch user data.', true);
      }
    });
  }

  private findEmergencyNumber(emergencyNumbers: any, organizationId: string | null): string | null {
    if (
      emergencyNumbers &&
      Array.isArray(emergencyNumbers.rows) &&
      organizationId
    ) {
      const match = emergencyNumbers.rows.find((item: any) => item.organizationId === organizationId);
      return match?.number || null;
    }
    return null;
  }

  private openDetailsModal(
    title: string,
    data: any,
    shouldCallApi: boolean
  ) {
    if (this.activeModalRef) {
      return this.activeModalRef.result;
    }

    const modalRef = this.modalService.open(DetailsModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top',
      keyboard: false,
    });

    this.activeModalRef = modalRef;

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.userInfo = data;
    modalRef.componentInstance.showCancel = false;
    modalRef.componentInstance.shouldCallApi = shouldCallApi;

    modalRef.result.then(
      () => { this.activeModalRef = null; },
      () => { this.activeModalRef = null; }
    );

    return modalRef.result;
  }

  private errorModal(title: string, data: any, showCancel: boolean = false) {
    const modalRef = this.modalService.open(DetailsModalComponent, {
      size: 'md',
      centered: true,
      backdrop: true,
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top',
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = data;
    modalRef.componentInstance.showCancel = showCancel;

    return modalRef.result;
  }

  playbackVideo(session: any) { }
}